import { Button, useGetList } from 'react-admin';
import { useNavigate } from 'react-router-dom';
import { Link } from 'react-router-dom';
import { Box, Typography, Paper, Chip, Grid, Avatar, Breadcrumbs, Link as MuiLink } from '@mui/material';
import type { ChipColor, ProgressColor } from '@/types/chip';
import { ArrowForward, CheckCircle, Warning } from '@mui/icons-material';
import { useEmployeeMap, getEmployeeName } from '@/hooks/useEmployeeMap';
import { STATUS_COLORS, ALERT_URGENCY_COLORS } from './constants';
import { DeemingCountdownBar } from './DeemingCountdownBar';

interface DashboardAssignment {
  id: string;
  status: string;
}

interface DashboardVendor {
  id: string;
  status: string;
  vendorName: string;
  activeWorkers?: number;
}

interface DashboardAlert {
  id: string;
  alertStatus: string;
  employee?: { id: string };
  vendorName?: string;
  tenureDays?: number;
  startDate?: string;
}

interface DashboardLiability {
  id: string;
  status: string;
}

export const TESDashboard = () => {
  const navigate = useNavigate();
  const employeeMap = useEmployeeMap();
  const { data: assignments } = useGetList('tes-assignments', { pagination: { page: 1, perPage: 500 } });
  const { data: vendors } = useGetList('tes-vendors', { pagination: { page: 1, perPage: 500 } });
  const { data: deemingAlerts } = useGetList('deeming-alerts', { pagination: { page: 1, perPage: 500 } });
  const { data: liabilityRecords } = useGetList('liability-records', { pagination: { page: 1, perPage: 500 } });

  const stats = {
    activeAssignments: assignments?.filter((a: DashboardAssignment) => a.status === 'Active').length ?? 0,
    deemingAlerts: deemingAlerts?.filter((a: DashboardAlert) => a.alertStatus !== 'Resolved').length ?? 0,
    openLiability: liabilityRecords?.filter((l: DashboardLiability) => l.status === 'Open' || l.status === 'Under Review').length ?? 0,
    activeVendors: vendors?.filter((v: DashboardVendor) => v.status === 'Active').length ?? 0,
  };

  const criticalAlerts = deemingAlerts
    ?.filter((a: DashboardAlert) => a.alertStatus === 'Escalated' || a.alertStatus === 'Pending Review')
    .sort((a: DashboardAlert, b: DashboardAlert) => (a.tenureDays ?? 0) - (b.tenureDays ?? 0))
    .slice(0, 5) ?? [];

  const vendorSummary = vendors?.slice(0, 5).map((v: DashboardVendor) => ({
    name: v.vendorName,
    status: v.status,
    workers: v.activeWorkers ?? 0,
  })) ?? [];

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>TES & Labour Broker Management</Typography>
      <Breadcrumbs sx={{ mb: 3 }}>
        <MuiLink component={Link} to="/" underline="hover" color="inherit">Home</MuiLink>
        <Typography color="text.primary">TES</Typography>
      </Breadcrumbs>

      <Grid container spacing={2} sx={{ mb: 3 }}>
        {[
          { label: 'Active Assignments', value: String(stats.activeAssignments), color: 'success.main' },
          { label: 'Deeming Alerts', value: String(stats.deemingAlerts), color: 'warning.main' },
          { label: 'Open Liability', value: String(stats.openLiability), color: 'error.main' },
          { label: 'Active Vendors', value: String(stats.activeVendors), color: 'info.main' },
        ].map(stat => (
          <Grid size={{ xs: 12, sm: 6, md: 3 }} key={stat.label}>
            <Paper sx={{ p: 3, borderTop: '4px solid', borderColor: stat.color, cursor: 'pointer' }}>
              <Typography variant="body2" color="text.secondary">{stat.label}</Typography>
              <Typography variant="h4" sx={{ mt: 0.5, fontWeight: 700 }}>{stat.value}</Typography>
            </Paper>
          </Grid>
        ))}
      </Grid>

      <Grid container spacing={2}>
        <Grid size={{ xs: 12, md: 7 }}>
          <Paper sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="subtitle2">Deeming Alerts (by urgency)</Typography>
              <Button component={Link} to="/deeming-alerts" size="small" endIcon={<ArrowForward />}>View All</Button>
            </Box>
            {criticalAlerts.length === 0 ? (
              <Box sx={{ p: 3, textAlign: 'center' }}>
                <CheckCircle sx={{ fontSize: 48, color: 'success.main', mb: 1 }} />
                <Typography color="text.secondary">No pending deeming alerts</Typography>
              </Box>
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                {criticalAlerts.map((alert: DashboardAlert) => (
                  <Box
                    key={alert.id}
                    sx={{ p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1, cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' } }}
onClick={() => navigate(`/deeming-alerts/${alert.id}/show`)}
                  >
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 0.5 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Avatar sx={{ width: 28, height: 28, bgcolor: ALERT_URGENCY_COLORS[alert.alertStatus] === 'error' ? 'error.main' : ALERT_URGENCY_COLORS[alert.alertStatus] === 'warning' ? 'warning.main' : 'info.main', fontSize: 12, color: 'white' }}>
                          {getEmployeeName(employeeMap, alert.employee?.id)?.charAt(0) || '?'}
                        </Avatar>
                        <Box>
                          <Typography variant="body2" fontWeight={600}>{getEmployeeName(employeeMap, alert.employee?.id)}</Typography>
                          <Typography variant="caption" color="text.secondary">{alert.vendorName}</Typography>
                        </Box>
                      </Box>
                      <Chip label={alert.alertStatus} size="small" color={ALERT_URGENCY_COLORS[alert.alertStatus] as ChipColor} />
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="caption" color="text.secondary">Tenure: {alert.tenureDays} days</Typography>
                      <DeemingCountdownBar startDate={alert.startDate ?? ''} thresholdDays={90} />
                    </Box>
                  </Box>
                ))}
              </Box>
            )}
          </Paper>
        </Grid>
        <Grid size={{ xs: 12, md: 5 }}>
          <Paper sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="subtitle2">Vendor Summary</Typography>
              <Button component={Link} to="/tesVendors" size="small" endIcon={<ArrowForward />}>View All</Button>
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              {vendorSummary.map((vendor, i) => (
                <Box
                  key={i}
                  sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Avatar sx={{ width: 28, height: 28, bgcolor: 'primary.main', fontSize: 12, color: 'white' }}>
                      {vendor.name?.charAt(0) || '?'}
                    </Avatar>
                    <Box>
                      <Typography variant="body2" fontWeight={500}>{vendor.name}</Typography>
                      <Typography variant="caption" color="text.secondary">{vendor.workers} active workers</Typography>
                    </Box>
                  </Box>
                  <Chip label={vendor.status} size="small" color={STATUS_COLORS[vendor.status] as ChipColor} />
                </Box>
              ))}
            </Box>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};
